// Single-leader election across same-origin windows via the Web Locks API.
//
// Exactly one open window holds the exclusive lock at a time — the "leader". It
// holds it for the window's lifetime; when that window closes, the lock frees
// and another window becomes leader. Used to gate background polling so only one
// window issues the request (its result reaches the others over the broadcast
// channel). Where the Web Locks API is unavailable, no window leads and polling
// simply doesn't run — periodic refresh is an enhancement, not load-bearing.

const LEADER_LOCK = "mp-refresh-leader";

let leader = false;
const listeners = new Set<() => void>();

function setLeader(value: boolean): void {
  if (leader === value) {
    return;
  }
  leader = value;
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Request the leader lock once for this window. Call once at app startup. The
 * lock is held (the callback promise never resolves) until the window closes,
 * at which point the browser releases it and another window can lead.
 */
export function setupRefreshLeader(): void {
  if (typeof navigator === "undefined" || !navigator.locks) {
    return;
  }
  navigator.locks.request(
    LEADER_LOCK,
    { mode: "exclusive" },
    () =>
      new Promise<void>(() => {
        setLeader(true);
      }),
  );
}

export function subscribeLeader(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

export function getIsLeader(): boolean {
  return leader;
}
